import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AutopayBadge } from "@/components/autopay/AutopayAdmin";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Download, Edit, Trash2, Mail, RefreshCw, RotateCcw, FileText, ExternalLink, CheckCircle2, X, FileSpreadsheet, FileDown, FolderArchive, DollarSign, Link2, MessageSquare } from "lucide-react";
import { isOverdue } from "./InvoiceFilters";
import { SortableHead, useSortedRows } from "@/components/SortableHeader";
import { usePermissions } from "@/hooks/usePermissions";

const STATUS_CLASS = {
  paid: "bg-green-100 text-green-800 border-green-200",
  pending: "bg-orange-100 text-orange-800 border-orange-200",
  sent: "bg-blue-100 text-blue-800 border-blue-200",
  partial: "bg-indigo-100 text-indigo-800 border-indigo-200",
  overdue: "bg-red-100 text-red-800 border-red-200",
};
const STATUS_LABEL = { partial: "PARTIALLY PAID" };

const money = (n) => `$${(n || 0).toFixed(2)}`;
export const paidAmount = (i) => (i.amount_paid != null ? i.amount_paid : i.status === "paid" ? i.total || 0 : 0);
export const balanceDue = (i) => Math.max((i.total || 0) - paidAmount(i), 0);

const csvCell = (v) => {
  const str = v == null ? "" : String(v);
  return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

export const exportInvoicesCsv = (rows) => {
  const header = ["Invoice #", "Customer", "Technician", "Invoice Date", "Due Date", "Status", "Overdue", "Recurring", "Subtotal", "Tax", "Total", "Amount Paid", "Balance Due", "Paid On"];
  const lines = rows.map((i) => [
    i.invoice_number, i.customer_name, i.technician_name || "", String(i.created_at || "").slice(0, 10), i.due_date, i.status,
    isOverdue(i) ? "yes" : "no", i.is_recurring ? "yes" : "no", (i.subtotal ?? 0).toFixed(2), (i.tax ?? 0).toFixed(2), (i.total ?? 0).toFixed(2),
    paidAmount(i).toFixed(2), balanceDue(i).toFixed(2), i.paid_at || "",
  ].map(csvCell).join(","));
  const blob = new Blob([[header.join(","), ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `invoices-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return rows.length;
};

const ACCESSORS = {
  invoice_number: (i) => i.invoice_number,
  customer_name: (i) => i.customer_name,
  technician_name: (i) => i.technician_name || "",
  created_at: (i) => String(i.created_at || ""),
  due_date: (i) => i.due_date || "",
  status: (i) => (isOverdue(i) ? "overdue" : i.status),
  total: (i) => i.total,
};

const StatusBadge = ({ invoice }) => (
  <div className="flex flex-wrap gap-1">
    <Badge className={STATUS_CLASS[invoice.status] || STATUS_CLASS.pending} data-testid="invoice-status-badge">{STATUS_LABEL[invoice.status] || invoice.status.toUpperCase()}</Badge>
    {isOverdue(invoice) && <Badge className={STATUS_CLASS.overdue} data-testid="invoice-overdue-badge">OVERDUE</Badge>}
    {invoice.is_recurring && (
      <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100"><RefreshCw className="w-3 h-3 mr-1" />RECURRING</Badge>
    )}
    {!invoice.is_recurring && invoice.recurring_stopped_at && (
      <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100" title="This invoice was part of a recurring series that was stopped" data-testid="recurring-stopped-badge"><RotateCcw className="w-3 h-3 mr-1" />Recurring stopped on {new Date(invoice.recurring_stopped_at).toLocaleDateString("en-US", { timeZone: "America/New_York" })}</Badge>
    )}
    <AutopayBadge invoice={invoice} />
  </div>
);

const Action = ({ onClick, className, title, testId, children }) => (
  <Button size="sm" variant="ghost" onClick={onClick} className={`h-8 px-2 ${className}`} title={title} data-testid={testId}>{children}</Button>
);

export const InvoiceTotalsBar = ({ invoices }) => {
  const total = invoices.reduce((s, i) => s + (i.total || 0), 0);
  const paid = invoices.reduce((s, i) => s + paidAmount(i), 0);
  const overdue = invoices.filter(isOverdue).reduce((s, i) => s + balanceDue(i), 0);
  const cells = [
    { label: "Filtered Total", value: money(total), cls: "text-slate-800", id: "totals-total" },
    { label: "Paid", value: money(paid), cls: "text-green-600", id: "totals-paid" },
    { label: "Outstanding", value: money(total - paid), cls: "text-orange-600", id: "totals-outstanding" },
    { label: "Overdue", value: money(overdue), cls: "text-red-600", id: "totals-overdue" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="invoice-totals-bar">
      {cells.map((c) => (
        <Card key={c.id} className="bg-white border-0 shadow">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wide text-slate-500">{c.label}</p>
            <p className={`text-xl font-bold ${c.cls}`} data-testid={c.id}>{c.value}</p>
          </CardContent>
        </Card>
      ))}
      <p className="sr-only">{invoices.length} invoices</p>
    </div>
  );
};

const BulkBar = ({ selected, onEmail, onMarkPaid, onPdf, onClear, busy, can }) => {
  const [confirmPaid, setConfirmPaid] = useState(false);
  const unpaid = selected.filter((i) => i.status !== "paid").length;
  return (
    <div className="flex flex-wrap items-center gap-3 p-3 mb-3 rounded-lg bg-blue-50 border border-blue-200" data-testid="bulk-action-bar">
      <p className="text-sm font-medium text-blue-900" data-testid="bulk-selected-count">{selected.length} selected</p>
      {can("invoices.send") && <Button size="sm" onClick={onEmail} disabled={busy} className="bg-orange-600 hover:bg-orange-700" data-testid="bulk-email-button">
        <Mail className="w-4 h-4 mr-1" /> {busy === "email" ? "Sending…" : "Email Selected"}
      </Button>}
      {can("invoices.mark_paid") && can("payments.record") && <Button size="sm" onClick={() => setConfirmPaid(true)} disabled={busy || unpaid === 0} className="bg-green-600 hover:bg-green-700" data-testid="bulk-mark-paid-button">
        <CheckCircle2 className="w-4 h-4 mr-1" /> Mark as Paid{unpaid ? ` (${unpaid})` : ""}
      </Button>}
      {can("invoices.export") && <Button size="sm" variant="outline" onClick={() => onPdf("zip")} disabled={busy} data-testid="bulk-pdf-zip-button">
        <FolderArchive className="w-4 h-4 mr-1" /> {busy === "pdf-zip" ? "Preparing…" : "PDFs (zip)"}
      </Button>}
      {can("invoices.export") && <Button size="sm" variant="outline" onClick={() => onPdf("merged")} disabled={busy} data-testid="bulk-pdf-merged-button">
        <FileDown className="w-4 h-4 mr-1" /> {busy === "pdf-merged" ? "Preparing…" : "Merged PDF"}
      </Button>}
      <Button size="sm" variant="ghost" onClick={onClear} disabled={busy} data-testid="bulk-clear-button"><X className="w-4 h-4 mr-1" /> Clear</Button>
      <AlertDialog open={confirmPaid} onOpenChange={setConfirmPaid}>
        <AlertDialogContent data-testid="bulk-mark-paid-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Mark {unpaid} invoice{unpaid === 1 ? "" : "s"} as paid?</AlertDialogTitle>
            <AlertDialogDescription>
              {selected.filter((i) => i.status !== "paid").map((i) => i.invoice_number).join(", ")} will be set to PAID. Already-paid invoices in the selection are skipped.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-mark-paid-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onMarkPaid} className="bg-green-600 hover:bg-green-700" data-testid="bulk-mark-paid-confirm">Mark as Paid</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export const InvoiceTable = ({ invoices, actions, selectedIds, onToggle, onToggleAll, onClearSelection, bulk, bulkBusy }) => {
  const { sorted, sort, toggle } = useSortedRows(invoices, ACCESSORS, "created_at", "desc");
  const { can } = usePermissions();
  const selected = invoices.filter((i) => selectedIds.has(i.id));
  const allChecked = invoices.length > 0 && selected.length === invoices.length;
  const someChecked = selected.length > 0 && !allChecked;
  const showBulk = can("invoices.bulk") || can("invoices.send") || can("invoices.export") || (can("invoices.mark_paid") && can("payments.record"));

  return (
    <div>
      <InvoiceTotalsBar invoices={invoices} />
      {selected.length > 0 && showBulk && (
        <BulkBar selected={selected} can={can} onEmail={() => bulk.email(selected)} onMarkPaid={() => bulk.markPaid(selected)} onPdf={(fmt) => bulk.pdf(selected, fmt)} onClear={onClearSelection} busy={bulkBusy} />
      )}
      <Card className="bg-white border-0 shadow-lg" data-testid="invoice-table-card">
        <CardContent className="p-0">
          <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100">
            <p className="text-sm text-slate-500">{sorted.length} row{sorted.length === 1 ? "" : "s"} · sorted by {sort.key.replace("_", " ")} {sort.dir === "asc" ? "↑" : "↓"}</p>
            {can("invoices.export") && <Button size="sm" variant="outline" onClick={() => bulk.exportCsv(sorted)} disabled={sorted.length === 0} data-testid="export-csv-button">
              <FileSpreadsheet className="w-4 h-4 mr-1" /> Export CSV
            </Button>}
          </div>
          <div className="overflow-x-auto">
          <Table data-testid="invoice-table">
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-10">
                  <Checkbox
                    checked={allChecked ? true : someChecked ? "indeterminate" : false}
                    onCheckedChange={(v) => onToggleAll(!!v, invoices)}
                    aria-label="Select all"
                    data-testid="invoice-select-all"
                  />
                </TableHead>
                <SortableHead label="Invoice #" sortKey="invoice_number" sort={sort} onToggle={toggle} />
                <SortableHead label="Customer" sortKey="customer_name" sort={sort} onToggle={toggle} />
                <SortableHead label="Technician" sortKey="technician_name" sort={sort} onToggle={toggle} className="hidden lg:table-cell" />
                <SortableHead label="Invoice Date" sortKey="created_at" sort={sort} onToggle={toggle} className="hidden md:table-cell" />
                <SortableHead label="Due Date" sortKey="due_date" sort={sort} onToggle={toggle} />
                <SortableHead label="Status" sortKey="status" sort={sort} onToggle={toggle} />
                <SortableHead label="Total" sortKey="total" sort={sort} onToggle={toggle} className="text-right" align="right" />
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="py-16 text-center" data-testid="invoice-table-empty">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-600 font-medium">No invoices match the current filters</p>
                  </TableCell>
                </TableRow>
              ) : (
                sorted.map((invoice) => (
                  <TableRow key={invoice.id} className={`hover:bg-slate-50 ${actions.onView ? "cursor-pointer" : ""} ${selectedIds.has(invoice.id) ? "bg-blue-50/60" : ""}`} data-testid="invoice-row" onClick={() => actions.onView && actions.onView(invoice)}>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={selectedIds.has(invoice.id)} onCheckedChange={() => onToggle(invoice.id)} aria-label={`Select ${invoice.invoice_number}`} data-testid={`invoice-select-${invoice.invoice_number}`} />
                    </TableCell>
                    <TableCell className="font-semibold text-slate-800 whitespace-nowrap">
                      <span className="text-blue-700 hover:underline" data-testid="invoice-number-link">{invoice.invoice_number}</span>
                      {invoice.stripe_invoice_url && (
                        <a href={invoice.stripe_invoice_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="ml-2 inline-flex text-purple-600 hover:text-purple-800" title="View Stripe invoice">
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-700">{invoice.customer_name}</TableCell>
                    <TableCell className="hidden lg:table-cell text-slate-600">{invoice.technician_name || <span className="text-slate-400">—</span>}</TableCell>
                    <TableCell className="hidden md:table-cell text-slate-600 whitespace-nowrap">{String(invoice.created_at || "").slice(0, 10)}</TableCell>
                    <TableCell className={`whitespace-nowrap ${isOverdue(invoice) ? "text-red-600 font-medium" : "text-slate-600"}`}>
                      {invoice.due_date}
                      {invoice.is_recurring && invoice.next_billing_reminder && <p className="text-xs text-purple-600">Next: {invoice.next_billing_reminder}</p>}
                    </TableCell>
                    <TableCell><StatusBadge invoice={invoice} /></TableCell>
                    <TableCell className={`text-right font-bold whitespace-nowrap ${invoice.status === "paid" ? "text-green-600" : "text-blue-600"}`}>
                      {money(invoice.total)}
                      {invoice.status !== "paid" && paidAmount(invoice) > 0 && (
                        <p className="text-xs font-medium text-indigo-600" data-testid="invoice-balance-due">Due {money(balanceDue(invoice))}</p>
                      )}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {actions.onRecordPayment && can("payments.record") && invoice.status !== "paid" && (
                        <Action onClick={() => actions.onRecordPayment(invoice)} className="text-emerald-600 hover:bg-emerald-50" title="Record payment" testId="record-payment-button"><DollarSign className="w-4 h-4" /></Action>
                      )}
                      {actions.onRecordPayment && (can("payments.view") || can("payments.attach_check")) && invoice.status === "paid" && (
                        <Action onClick={() => actions.onRecordPayment(invoice)} className="text-slate-400 hover:bg-slate-100" title="Payment history" testId="payment-history-button"><CheckCircle2 className="w-4 h-4" /></Action>
                      )}
                      <Action onClick={() => actions.downloadPDF(invoice.id, invoice.invoice_number)} className="text-green-600 hover:bg-green-50" title="Download PDF" testId="download-pdf-button"><Download className="w-4 h-4" /></Action>
                      {can("invoices.send") && <Action onClick={() => actions.emailInvoice(invoice.id)} className="text-orange-600 hover:bg-orange-50" title="Email invoice" testId="email-invoice-button"><Mail className="w-4 h-4" /></Action>}
                      {actions.textInvoice && can("invoices.send") && can("messages.send") && <Action onClick={() => actions.textInvoice(invoice.id)} className="text-sky-600 hover:bg-sky-50" title="Send by Text" testId="text-invoice-button"><MessageSquare className="w-4 h-4" /></Action>}
                      {actions.copyPayLink && <Action onClick={() => actions.copyPayLink(invoice)} className="text-sky-600 hover:bg-sky-50" title="Copy View & Pay link" testId="copy-pay-link-button"><Link2 className="w-4 h-4" /></Action>}
                      {can("invoices.edit") && <Action onClick={() => actions.onEdit(invoice)} className="text-blue-600 hover:bg-blue-50" title="Edit" testId="edit-invoice-button"><Edit className="w-4 h-4" /></Action>}
                      {can("invoices.recurring_manage") && invoice.status !== "paid" && (invoice.is_recurring ? (
                        actions.unmarkAsRecurring && <Action onClick={() => actions.unmarkAsRecurring(invoice.id)} className="text-gray-600 hover:bg-gray-100" title="Stop recurring" testId="stop-recurring-button"><RotateCcw className="w-4 h-4" /></Action>
                      ) : (
                        <Action onClick={() => actions.markAsRecurring(invoice.id)} className="text-purple-600 hover:bg-purple-50" title="Make recurring" testId="make-recurring-button"><RefreshCw className="w-4 h-4" /></Action>
                      ))}
                      {can("invoices.delete") && <Action onClick={() => actions.onDelete(invoice.id)} className="text-red-600 hover:bg-red-50" title="Delete" testId="delete-invoice-button"><Trash2 className="w-4 h-4" /></Action>}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
